import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { useAuthStore } from '../../stores/auth';

import { userLanding } from '../../lib/profileAccess';
import { applyBrandToDocument, tenantBrandFor } from '../../lib/tenantBrand';

/** Login wordmark height (px). Width follows the file's aspect ratio. */
const BRAND_LOGO_HEIGHT = 64;

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { login, error, isLoading, user, clearError, tenant } = useAuthStore();
  const navigate = useNavigate();
  // Pre-auth there is usually no tenant yet — the hostname picks the brand.
  const brand = tenantBrandFor(tenant?.slug);
  // The login route sits outside ThemeProvider; ThemeProvider leaves `.dark`
  // on <html> when the user picked dark mode, so read it from there.
  const darkSurface = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');

  useEffect(() => { applyBrandToDocument(brand); }, [brand]);

  // If already logged in, redirect to department-appropriate dashboard
  useEffect(() => {
    if (user && !isLoading) {
      navigate(userLanding(user), { replace: true });
    }
  }, [user, isLoading, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await login(email, password);
      // Use window.location for a clean navigation (avoids React Router race conditions)
      const u = useAuthStore.getState().user;
      window.location.href = userLanding(u);
    } catch {}
  };

  return (
    <div
      className="aura-root flex min-h-screen items-center justify-center px-4"
      style={{ background: 'var(--ivory-0)' }}
    >
      <div className="w-full max-w-md">
        {brand ? (
        <div className="mb-8 text-center">
          <img
            src={darkSurface ? brand.logoOnDark : brand.logoOnLight}
            alt={brand.name}
            width={Math.round(BRAND_LOGO_HEIGHT * brand.logoAspect)}
            height={BRAND_LOGO_HEIGHT}
            decoding="async"
            className="mx-auto block"
            style={{ height: BRAND_LOGO_HEIGHT, width: 'auto', maxWidth: '100%', objectFit: 'contain' }}
          />
          <h1
            className="text-2xl"
            style={{
              marginTop: 20,
              fontFamily: 'var(--ff-serif)',
              fontWeight: 600,
              color: 'var(--ink-0)',
              letterSpacing: '-0.01em',
            }}
          >
            Sign in
          </h1>
        </div>
        ) : (
        <div className="mb-8 text-center">
          <div
            className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl text-xl font-bold"
            style={{
              background: 'linear-gradient(135deg, var(--ink-0), var(--ink-1))',
              color: 'var(--ivory-0)',
              fontFamily: 'var(--ff-serif)',
            }}
          >
            A
          </div>
          <h1
            className="mt-4 text-2xl"
            style={{
              fontFamily: 'var(--ff-serif)',
              fontWeight: 600,
              color: 'var(--ink-0)',
              letterSpacing: '-0.01em',
            }}
          >
            Sign in to AURA
          </h1>
          <p className="mt-1 text-sm" style={{ color: 'var(--ink-3)' }}>
            AI Unified Resource Architecture
          </p>
        </div>
        )}

        <form
          onSubmit={handleSubmit}
          className="rounded-xl p-8"
          style={{
            background: 'var(--ivory-1)',
            border: '1px solid var(--line)',
            boxShadow: 'var(--sh-2)',
          }}
        >
          {error && (
            <div
              className="mb-4 rounded-lg p-3 text-sm"
              style={{ background: 'var(--danger-bg)', color: 'var(--danger)' }}
            >
              {error}
              <button onClick={clearError} className="ml-2 font-medium underline">Dismiss</button>
            </div>
          )}

          <div className="mb-4">
            <label htmlFor="email" className="mb-1.5 block text-sm font-medium" style={{ color: 'var(--ink-1)' }}>
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full rounded-lg px-3.5 py-2.5 text-sm focus:outline-none"
              style={{
                background: 'var(--ivory-0)',
                color: 'var(--ink-0)',
                border: '1px solid var(--line)',
              }}
              placeholder="you@company.com"
            />
          </div>

          <div className="mb-6">
            <label htmlFor="password" className="mb-1.5 block text-sm font-medium" style={{ color: 'var(--ink-1)' }}>
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full rounded-lg px-3.5 py-2.5 pr-10 text-sm focus:outline-none"
                style={{
                  background: 'var(--ivory-0)',
                  color: 'var(--ink-0)',
                  border: '1px solid var(--line)',
                }}
                placeholder="Enter your password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2"
                style={{ color: 'var(--ink-3)' }}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {/* AI-vbof (Feature B, Gap 2) — self-service reset link. */}
            <div className="mt-1.5 text-right">
              <Link
                to="/forgot-password"
                className="text-xs font-medium"
                style={{ color: 'var(--saffron-600)' }}
              >
                Forgot password?
              </Link>
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors disabled:opacity-50"
            style={{
              background: 'var(--ink-0)',
              color: 'var(--ivory-0)',
            }}
            onMouseOver={(e) => {
              if (!isLoading) (e.currentTarget as HTMLButtonElement).style.background = 'var(--ink-1)';
            }}
            onMouseOut={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = 'var(--ink-0)';
            }}
          >
            {isLoading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>

        <p className="mt-6 text-center text-xs text-gray-400">
          {brand ? 'Powered by AURA · Ardhanex Tech Pvt Ltd' : 'Powered by Ardhanex Tech Pvt Ltd'}
        </p>
      </div>
    </div>
  );
}
